import { Fragment, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { PALETTES } from "@/components/palettes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HALO = "0 0 120px rgba(194,67,159,0.13), 0 0 36px rgba(122,79,208,0.10)";

const ArtFrame = ({ palette, label, className = "", number, logo, logoAlt, cover, coverAlt, coverFit, padded, halo = false }) => {
  const p = PALETTES[palette] || PALETTES.chrome;
  const contain = coverFit === "contain";
  const natural = (cover || logo) && coverFit !== "cover";
  const aspects = className.match(/aspect-\[[^\]]+\]|aspect-square/g) || [];
  const wrapperCls = (className.replace(/aspect-\[[^\]]+\]|aspect-square/g, "").replace(/\s+/g, " ").trim() + " self-start").trim();
  // Captions always sit beneath the artwork in normal document flow — no overlays.
  const captionBelow = label && (
    <span className="mt-2.5 block text-[10px] uppercase tracking-[0.26em] text-charcoal-soft/80">
      {label}
    </span>
  );
  // Natural mode: artwork renders at its own proportions — no fixed frame, no letterbox bars.
  // Palette background still sits directly behind the art (for transparent marks) but never extends past it.
  if (natural) {
    return (
      <div className={wrapperCls}>
        <div className="relative overflow-hidden border border-charcoal/10" style={{ background: p.bg, ...(halo ? { boxShadow: HALO } : {}) }}>
          <img
            src={cover || logo}
            alt={coverAlt || logoAlt || ""}
            loading="lazy"
            className={`block w-full h-auto ${padded ? "p-[8%]" : ""}`}
          />
        </div>
        {captionBelow}
      </div>
    );
  }
  return (
    <div className={wrapperCls}>
      <div className={`relative overflow-hidden border border-charcoal/10 ${aspects.join(" ")}`} style={{ background: cover && !contain ? "#171514" : p.bg, ...(halo ? { boxShadow: HALO } : {}) }}>
        {cover && (
          contain && padded ? (
            <span className="absolute inset-0 flex items-center justify-center p-[8%]">
              <img src={cover} alt={coverAlt || ""} loading="lazy" className="max-w-full max-h-full object-contain" />
            </span>
          ) : (
            <img src={cover} alt={coverAlt || ""} loading="lazy" className={`absolute inset-0 w-full h-full ${contain ? "object-contain" : "object-cover"}`} />
          )
        )}
        {!cover && logo && (
          <span className="absolute inset-0 flex items-center justify-center p-[10%]">
            <img src={logo} alt={logoAlt || ""} loading="lazy" className="max-w-full max-h-full object-contain" />
          </span>
        )}
        {!cover && !logo && number && (
          <span
            aria-hidden="true"
            className={`absolute -bottom-[10%] left-[4%] font-serif leading-none text-[30vmin] select-none ${p.numberChrome ? "text-chrome" : ""}`}
            style={p.numberChrome ? {} : { color: p.number, opacity: 0.9 }}
          >
            {number}
          </span>
        )}
      </div>
      {captionBelow}
    </div>
  );
};

const FlagshipStudy = ({ project }) => {
  const study = project.study || {};
  const storyArt = project.story_art || {};
  const chapters = project.chapters?.length ? project.chapters : null;
  const sections = chapters
    ? chapters.map((c) => [c.label, c.body, c.image])
    : [
        ["The Challenge", study.challenge],
        ["The Direction", study.direction],
        ["The Process", study.process],
        ["The Result", study.result],
      ].filter(([, body]) => body);

  const challengeImg = storyArt.challenge || project.cover || project.logo;
  const directionLogo = storyArt.direction_logo || project.logo;
  const directionBg = project.direction_bg || "#160a17";
  const brandColors = project.brand_colors || [];
  const processImages = storyArt.process || (project.plates || []).slice(0, 3).map((p) => ({ src: p.src, label: p.label }));

  return (
    <div data-testid="flagship-case-study">
      <div className="grid grid-cols-12">
        <ArtFrame halo palette={project.palette} number={project.number} logo={project.logo} logoAlt={`${project.title} logo`} cover={project.cover} coverAlt={project.title} coverFit={project.cover_fit} label={sections.length ? "Flagship — Case Study" : "Flagship"} className={`col-span-12 ${COVER_FRAMES[project.cover_frame] || "aspect-[16/9]"} mb-20 md:mb-32`} />
      </div>
      {chapters ? (
        sections.map(([label, body, img], i) => (
        <Reveal key={label} delay={0.05}>
          {img ? (
            <div className="grid grid-cols-12 gap-8 md:gap-12 items-center border-t border-charcoal/15 py-12 md:py-20">
              <div className={`col-span-12 md:col-span-7 ${i % 2 === 0 ? "md:order-2" : ""}`}>
                <ArtFrame palette={img.palette || "white"} cover={img.src} coverAlt={img.label} coverFit={img.fit || "contain"} label={img.label} className="aspect-[4/3]" />
              </div>
              <div className={`col-span-12 md:col-span-5 ${i % 2 === 0 ? "md:order-1" : ""}`}>
                <span className="block font-serif italic text-4xl md:text-6xl text-charcoal/15 mb-4">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-5">{label}</h2>
                <p className="text-sm md:text-base text-charcoal-soft leading-relaxed">{body}</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-6 border-t border-charcoal/15 py-14 md:py-20">
              <span className="col-span-12 md:col-span-2 font-serif italic text-4xl md:text-6xl text-charcoal/15">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h2 className="col-span-12 md:col-span-4 font-serif text-3xl md:text-4xl tracking-tight">{label}</h2>
              <p className="col-span-12 md:col-span-5 md:col-start-8 text-sm md:text-base text-charcoal-soft leading-relaxed">
                {body}
              </p>
            </div>
          )}
        </Reveal>
        ))
      ) : (
        <>
          {/* 01 — The Challenge: black panel, headline + copy beside a corporate application */}
          {study.challenge && (
            <Reveal delay={0.05}>
              <section data-testid="story-challenge" className="relative overflow-hidden bg-[#0A0A0C] text-[#f6f0e6]">
                <div className="grid md:grid-cols-2 items-center gap-10 md:gap-16 px-7 py-14 md:px-14 md:py-20 lg:px-20">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.34em] text-[#f6f0e6]/50 mb-6">01 — The Challenge</p>
                    <h2 className="font-serif text-5xl md:text-7xl tracking-tight leading-[0.95]">
                      The <span className="italic">Challenge.</span>
                    </h2>
                    <p className="mt-7 text-sm md:text-base text-[#f6f0e6]/75 leading-relaxed max-w-md">
                      {study.challenge}
                    </p>
                  </div>
                  {challengeImg && (
                    <div className="border border-[#f6f0e6]/12 overflow-hidden" data-testid="story-challenge-art">
                      <img src={challengeImg} alt={`${project.title} — corporate application`} loading="lazy" className="block w-full h-auto max-h-[540px] object-contain bg-[#0A0A0C]" />
                    </div>
                  )}
                </div>
              </section>
            </Reveal>
          )}

          {/* 02 — The Direction: the brand's own color environment, approved logo, real color samples */}
          {study.direction && (
            <Reveal delay={0.05}>
              <section
                data-testid="story-direction"
                className="relative overflow-hidden mt-6 md:mt-8"
                style={{ background: `linear-gradient(155deg, ${directionBg} 0%, ${directionBg} 55%, rgba(0,0,0,0.55) 100%), linear-gradient(${directionBg}, ${directionBg})` }}
              >
                <div className="px-7 py-14 md:px-14 md:py-20 lg:px-20">
                  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 border-b border-[#F1F2F5]/25 pb-8">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.34em] text-[#F1F2F5]/60 mb-6">02 — The Direction</p>
                      <h2 className="font-serif text-5xl md:text-7xl tracking-tight leading-[0.95] text-[#F1F2F5]">
                        The <span className="italic">Direction.</span>
                      </h2>
                    </div>
                    <p className="text-sm md:text-base text-[#F1F2F5]/85 leading-relaxed max-w-md">
                      {study.direction}
                    </p>
                  </div>
                  {directionLogo && (
                    <div className="py-12 md:py-16 flex justify-center" data-testid="story-direction-logo">
                      <div className="bg-[#F6F2EA] px-8 py-10 md:px-14 md:py-12 w-[min(640px,92%)] flex justify-center">
                        <img src={directionLogo} alt={`${project.title} — approved logo`} loading="lazy" className="w-full h-auto" />
                      </div>
                    </div>
                  )}
                  {brandColors.length > 0 && (
                    <div data-testid="story-direction-colors" className="grid grid-cols-3 md:grid-cols-6 gap-px bg-[#F1F2F5]/20 border border-[#F1F2F5]/20">
                      {brandColors.map((c) => (
                        <div key={c.hex} className="p-4 md:p-5" style={{ background: "rgba(0,0,0,0.3)" }}>
                          <span className="block w-full h-10 md:h-12 mb-3 border border-[#F1F2F5]/25" style={{ background: c.hex }} />
                          <p className="text-[9px] uppercase tracking-[0.22em] text-[#F1F2F5]/85">{c.name}</p>
                          <p className="text-[9px] tracking-[0.12em] text-[#F1F2F5]/50 mt-0.5">{c.hex}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            </Reveal>
          )}

          {/* 03 — The Process: intro across the top, then two aligned panels */}
          {study.process && (
            <Reveal delay={0.05}>
              <section data-testid="story-process" className="relative overflow-hidden mt-6 md:mt-8 bg-[#F3EDE3]">
                <div className="px-7 py-14 md:px-14 md:py-20 lg:px-20">
                  <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-end">
                    <div className="md:col-span-5">
                      <p className="text-[10px] uppercase tracking-[0.34em] text-charcoal-mute mb-6">03 — The Process</p>
                      <h2 className="font-serif text-5xl md:text-6xl tracking-tight leading-[0.95] text-charcoal">
                        The <span className="italic">Process.</span>
                      </h2>
                    </div>
                    <p className="md:col-span-6 md:col-start-7 text-sm md:text-base text-charcoal-soft leading-relaxed">
                      {study.process}
                    </p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4 md:gap-5 mt-10 md:mt-14 items-stretch" data-testid="story-process-art">
                    {processImages.slice(0, 2).map((img) => (
                      <figure key={img.src} className="flex flex-col">
                        <div className="bg-[#0A0A0C] flex items-center justify-center p-8 md:p-12 aspect-[4/3] md:aspect-[16/10]">
                          <img src={img.src} alt={img.label} loading="lazy" className="max-w-full max-h-full w-auto h-auto object-contain" />
                        </div>
                        <figcaption className="text-[10px] uppercase tracking-[0.22em] text-charcoal-soft/80 pt-3">
                          {img.label}
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                </div>
              </section>
            </Reveal>
          )}

          {/* 04 — The Result: closing statement */}
          {study.result && (
            <Reveal delay={0.05}>
              <div data-testid="story-result" className="mt-16 md:mt-24 max-w-4xl">
                <hr className="chrome-rule w-20 mb-8" />
                <p className="text-[10px] uppercase tracking-[0.34em] text-charcoal-mute mb-6">04 — The Result</p>
                <p className="font-serif italic text-2xl md:text-4xl leading-[1.2] tracking-tight text-charcoal">
                  {study.result}
                </p>
              </div>
            </Reveal>
          )}
        </>
      )}
      {project.plates?.length > 0 && (
        <div data-testid="flagship-plates" className="grid grid-cols-12 gap-5 md:gap-6 lg:gap-8 mt-20 md:mt-28">
          {project.plates.map((plate, i) => (
            <Fragment key={plate.src}>
              {plate.section && (
                <div data-testid={`plate-section-${plate.section.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="col-span-12 flex items-baseline justify-between border-t border-charcoal/15 pt-6 mt-4 md:mt-8">
                  <p className="text-[11px] uppercase tracking-[0.34em] text-charcoal-mute">{plate.section}</p>
                </div>
              )}
              <ArtFrame
                palette={plate.palette}
                cover={plate.src}
                coverAlt={plate.label}
                coverFit={plate.fit || "contain"}
                label={plate.label}
                className={
                  project.plates.length === 1
                    ? "col-span-12 md:col-span-8 md:col-start-3 aspect-[4/3]"
                    : (plate.frame === "wide16" ? `col-span-12 ${COVER_FRAMES.wide16}` : PLATE_DEFAULTS[i]?.cls || "col-span-12 md:col-span-6 aspect-[4/3]")
                }
              />
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

const COVER_FRAMES = {
  portrait: "md:col-span-8 md:col-start-3 aspect-[4/5]",
  square: "md:col-span-6 md:col-start-4 aspect-square",
  feature: "md:col-span-8 md:col-start-3 aspect-square",
  deck: "md:col-span-12 aspect-[4/3]",
  wide: "md:col-span-12 aspect-[21/9]",
  wide16: "md:col-span-12 aspect-[16/9]",
};

const PLATE_DEFAULTS = [
  { cls: "md:col-span-7 aspect-[4/3]" },
  { cls: "md:col-span-5 aspect-[4/3] lg:mt-20" },
  { cls: "md:col-span-12 aspect-[21/9]" },
];

const StandardGallery = ({ project }) => {
  const plates = project.plates || [];
  const fallbacks = ["md:col-span-7 aspect-[4/3]", "md:col-span-5 aspect-[4/3] lg:mt-20", "md:col-span-12 aspect-[21/9]", "md:col-span-6 md:col-start-4 aspect-[4/3]"];
  return (
    <div data-testid="standard-gallery" className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6 lg:gap-8">
      <ArtFrame halo palette={project.palette} number={project.number} logo={project.logo} logoAlt={`${project.title} logo`} cover={project.cover} coverAlt={project.title} coverFit={project.cover_fit} label={project.cover ? "Key Art" : "Identity"} className={COVER_FRAMES[project.cover_frame] || "md:col-span-12 aspect-[16/9]"} />
      {plates.map((plate, i) => (
        <Fragment key={plate.src}>
          {plate.section && (
            <div data-testid={`plate-section-${plate.section.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="col-span-1 md:col-span-12 flex items-baseline justify-between border-t border-charcoal/15 pt-5 mt-4 md:mt-10 lg:mt-14">
              <p className="text-[11px] uppercase tracking-[0.34em] text-charcoal-mute">{plate.section}</p>
            </div>
          )}
          <ArtFrame palette={plate.palette} cover={plate.src} coverAlt={plate.label} coverFit={plate.fit || "contain"} padded={plate.padded} label={plate.label} className={plate.frame ? COVER_FRAMES[plate.frame] : fallbacks[i % fallbacks.length]} />
        </Fragment>
      ))}
    </div>
  );
};

const FlyerWall = ({ flyers, title = "Flyers + Campaign", testid = "flyer-wall" }) => (
  <div data-testid={testid} className="mt-24 md:mt-32">
    <Reveal>
      <div className="flex items-baseline justify-between border-t border-charcoal/15 pt-8 mb-10 md:mb-14">
        <p className="text-[11px] uppercase tracking-[0.34em] text-charcoal-mute">{title}</p>
        <p className="text-[11px] uppercase tracking-[0.34em] text-charcoal-mute">{String(flyers.length).padStart(2, "0")}</p>
      </div>
    </Reveal>
    <div className="flex flex-wrap justify-center items-start gap-5 md:gap-8">
      {flyers.map((f, i) => (
        <Reveal key={f.src} delay={(i % 3) * 0.08} className={flyers.length === 1 ? "w-full max-w-sm md:max-w-md" : "w-full sm:w-[calc(50%-0.625rem)] lg:w-[calc(33.333%-1.34rem)]"}>
          <div>
            <div className="group relative overflow-hidden border border-charcoal/10">
              <img
                src={f.src}
                alt={f.label}
                loading="lazy"
                className="block w-full h-auto transition-transform duration-700 ease-out group-hover:scale-[1.01]"
              />
            </div>
            <span className="mt-2.5 block text-[10px] uppercase tracking-[0.24em] text-charcoal-soft/80">
              {f.label}
            </span>
          </div>
        </Reveal>
      ))}
    </div>
  </div>
);

export default function ProjectPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setData(null);
    setMissing(false);
    axios
      .get(`${API}/projects/${slug}`)
      .then((res) => setData(res.data))
      .catch(() => setMissing(true));
  }, [slug]);

  return (
    <main>
      <Nav />
      <motion.div
        key={slug}
        data-testid="project-page"
        data-project-title={data ? data.project.title : undefined}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="px-6 md:px-12 lg:px-24 pt-32 md:pt-44 pb-28 min-h-screen"
      >
        <Link
          data-testid="project-back-link"
          to="/"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-charcoal-mute hover:text-charcoal transition-colors duration-300 mb-12"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.25} /> All work
        </Link>

        {missing && (
          <div data-testid="project-not-found">
            <h1 className="font-serif text-4xl md:text-6xl">Not in the edit.</h1>
            <p className="mt-6 text-charcoal-soft">This project doesn't exist — yet.</p>
          </div>
        )}

        {data && (
          <>
            {/* Purple-chrome framing — slim brand band opening every project page */}
            <div
              aria-hidden="true"
              data-testid="project-chrome-band"
              className="h-[2px] w-full mb-12 md:mb-16"
              style={{ background: "linear-gradient(90deg, transparent 0%, rgba(194,67,159,0.5) 28%, rgba(122,79,208,0.5) 58%, rgba(229,184,214,0.55) 78%, transparent 100%)" }}
            />
            <Reveal>
              <header className="mb-16 md:mb-24">
                <p className="text-[11px] uppercase tracking-[0.34em] text-charcoal-mute mb-6">
                  No. {data.project.number} — {data.project.series} · {data.project.year}
                </p>
                <h1 className="font-serif text-5xl md:text-8xl leading-[0.95] tracking-tight">
                  {data.project.title.includes("—") ? (
                    <>
                      {data.project.title.split("—")[0]}
                      <span className="italic text-chrome">— {data.project.title.split("—")[1]}</span>
                    </>
                  ) : (
                    data.project.title
                  )}
                </h1>
                {data.project.subtitle && (
                  <p data-testid="project-subtitle" className="mt-4 text-[11px] md:text-xs uppercase tracking-[0.3em] text-charcoal-mute">
                    {data.project.subtitle}
                  </p>
                )}
                <p className="mt-8 max-w-xl text-sm md:text-base text-charcoal-soft leading-relaxed">
                  {data.project.summary}
                </p>
                <p className="mt-4 text-[11px] uppercase tracking-[0.26em] text-charcoal-mute">
                  {data.project.tags.join(" · ")}
                </p>
              </header>
            </Reveal>

            {data.project.kind === "flagship" ? (
              <FlagshipStudy project={data.project} />
            ) : (
              <StandardGallery project={data.project} />
            )}

            {data.project.flyers?.length > 0 && <FlyerWall flyers={data.project.flyers} />}

            {data.project.apparel?.length > 0 && <FlyerWall flyers={data.project.apparel} title="Clothing Campaign" testid="apparel-wall" />}

            <div className="mt-24 md:mt-32 flex justify-end">
              <Link
                data-testid="project-next-link"
                to={`/work/${data.next_slug}`}
                className="group inline-flex items-baseline gap-4"
              >
                <span className="text-[11px] uppercase tracking-[0.3em] text-charcoal-mute">Next Edit</span>
                <span className="font-serif italic text-3xl md:text-5xl group-hover:text-chrome transition-colors duration-300">
                  {data.next_title || `No. ${data.next_number}`}
                </span>
                <ArrowRight className="w-5 h-5 self-center text-charcoal-mute group-hover:translate-x-1 transition-transform duration-300" strokeWidth={1.25} />
              </Link>
            </div>
          </>
        )}
      </motion.div>
      <Footer />
    </main>
  );
}
